import { FilterGroup } from "@/components-v2/filterDialogDomain";
import { HttpEvent, HttpEventCallbacks, httpGet, httpPost } from "@/events/eventsDomain";
import { CompanyFieldInfo } from "@/companyField/companyFieldDomain";
import { AccessType } from "@/types/enums/AccessType";
import { RoleType } from "@/types/enums/RoleType";
import { CompanyroleId } from "@/types/CompanyInfo";
import { PhoneNumber } from "@/common/common";
import { CustomRole } from "@/common/customRole";
import type { TagColor } from "@/components-v2/Tag";

export type PortfolioOverviewDTO = {
  portfolios: PortfolioDTO[];
  companies: CompanyDTO[];
  contacts: ContactDTO[];
  customRoles: CustomRole[];
  portfolioCompanies: PortfolioCompanyLink[];
  companyAccess: CompanyAccessDTO[];
  companyRoles: CompanyRoleLink[];
  ownerships: OwnershipLink[];
  recentProjects: RecentProject[];
};

export type PortfolioDTO = {
  id: string;
  name: string;
  ownerExternalId: string;
  isOwner: boolean;
  createdAt: string;
};

export type CompanyDTO = {
  orgNo: string;
  name: string;
  currency: string;
  lastVisitedAt?: string;
};

export type RecentProject = {
  projectId: string;
  orgNo: string;
  name: string;
  updatedAt: string;
};

export type ContactDTO = {
  contactId: string;
  name: string;
  isCompany: boolean;
  email?: string;
  phoneNumber?: PhoneNumber;
};

export type PortfolioCompanyLink = {
  portfolioId: string;
  orgNo: string;
};

export type CompanyAccessDTO = {
  orgNo: string;
  accessType: AccessType;
  isStarred: boolean;
  shareholderId?: number;
  customRoleId?: string;
};

export type CompanyRoleLink = {
  companyRoleId: CompanyroleId;
  contactId: string;
  orgNo: string;
  roleType: RoleType;
};

export type OwnershipLink = {
  shareholderId: number;
  contactId: string;
  orgNo: string;
};

export type ReportSearchRequest = {
  query: { text: string };
  date?: string;
  filter?: FilterGroup<string>;
};

export type ReportTag = { text: string; color: TagColor };

export type PlainReportValue = string | number;

export type ReportValue = PlainReportValue | ReportTag;

export type ReportRow = { id: string; orgNo: string } & Record<string, ReportValue | undefined>;

export type ReportRoleField = Pick<CompanyFieldInfo, "type" | "format" | "asOf"> &
  (
    | { source: "ROLE"; role: RoleType }
    | {
        source: "ACCOUNT_ROLE";
        roleTypeId: string;
        accountId: string;
        accountName: string;
        name: string;
      }
  );

export type ReportField = CompanyFieldInfo | ReportRoleField;

export type ReportSearchResult = {
  date: string;
  fields: Record<string, ReportField>;
  data: ReportRow[];
};

export type PortfolioV2ApiClient = {
  getOverview: (
    params: HttpEventCallbacks<PortfolioOverviewDTO>,
  ) => HttpEvent<PortfolioOverviewDTO>;
  search: (
    request: ReportSearchRequest,
    params: HttpEventCallbacks<ReportSearchResult>,
  ) => HttpEvent<ReportSearchResult>;
};

export const portfolioV2ApiClient: PortfolioV2ApiClient = {
  getOverview: (params) => httpGet({ url: "/v2/portfolio", ...params }),
  search: (request, params) => httpPost({ url: "/v2/portfolio/search", data: request, ...params }),
};
